import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8011';

const AddLedger = () => {
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [person, setPerson] = useState('');
  const [project, setProject] = useState('');
  const [type, setType] = useState(''); // Transaction type (Credit/Debit)
  const [persons, setPersons] = useState([]);
  const [projects, setProjects] = useState([]);
  const [fileList, setFileList] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPersons = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/persons/getAllPersons`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch persons');
        }

        const data = await response.json();
        if (Array.isArray(data)) {
          setPersons(data);
        } else {
          throw new Error('Persons data is not an array');
        }
      } catch (error) {
        console.error('Error fetching persons:', error);
        toast.error('Failed to load persons');
      }
    };
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/projects/getAllProjects`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }

        const data = await response.json();
        if (Array.isArray(data)) {
          setProjects(data);
        } else {
          throw new Error('Projects data is not an array');
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to load projects');
      }
    };
    const currentDate = new Date().toISOString().split('T')[0];
    setDate(currentDate);

    fetchPersons();
    fetchProjects();
  }, []);


  const handleImageChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const uploadImage = async (image) => {
    const data = new FormData();
    data.append('file', image);
    data.append('upload_preset', 'DecentBuilders');
    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/dwbwxh3sj/image/upload', {
        method: 'POST',
        body: data,
      });
      const urlData = await response.json();
      return urlData.url;
    } catch (error) {
      console.error(error);
      toast.error('Image upload failed.');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to add in Ledger.');
      navigate('/signIn');
      return;
    }
  
    let imageUrl = '';
    if (fileList.length > 0) {
      imageUrl = await uploadImage(fileList[0].originFileObj);
      if (!imageUrl) return;
    }
  
    // Determine balance adjustment based on transaction type
    const balanceAdjustment = type === 'Credit' ? amount : -amount;
  
    const ledgerData = {
      title,
      description,
      price: Number(amount),
      person,
      project,
      date,
      type,
      balance: balanceAdjustment, // Include balance adjustment
      imagePath: imageUrl,
    };
  
    try {
      const response = await fetch(`${API_BASE_URL}/ledger/addLedger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(ledgerData),
      });
  
      if (response.ok) {
        toast.success('Ledger saved successfully!');
        setTitle('');
        setAmount('');
        setDescription('');
        setDate(new Date().toISOString().split('T')[0]);
        setPerson('');
        setProject('');
        setType('');
        setFileList([]);
      } else {
        const errorText = await response.text();
        toast.error(`Failed to save ledger: ${errorText}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while saving ledger.');
    }
  };
  

  return (
    <div className="w-full mx-auto p-4 bg-white h-full">
      <h2 className="text-2xl font-semibold mb-6 text-themeColor2">Add Ledger</h2>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <div>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">
                Title:
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                />
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2">
                Amount:
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                />
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2">
                Project:
                <select
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                >
                  <option value="">Select Project</option>
                  {projects.map((pro) => (
                    <option key={pro._id} value={pro.project}>
                      {pro.project}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">
                Person:
                <select
                  value={person}
                  onChange={(e) => setPerson(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                >
                  <option value="">Select Person</option>
                  {persons.map((per) => (
                    <option key={per._id} value={per.person}>
                      {per.person}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2">
                Date:
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                />
              </label>
            </div>
          </div>

          <div>
          
          
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <div className="mb-4">
              <label className="block text-gray-700 mb-2">
                Transaction Type:
              </label>
              <div>
                <label className="inline-flex items-center mr-4">
                  <input
                    type="radio"
                    value="Credit"
                    checked={type === 'Credit'}
                    onChange={() => setType('Credit')}
                    className="form-radio bg-themeColor1 text-themeColor2"
                  />
                  <span className="ml-2">Credit</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="Debit"
                    checked={type === 'Debit'}
                    onChange={() => setType('Debit')}
                    className="form-radio"
                  />
                  <span className="ml-2">Debit</span>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2">
                Image (Optional):
                <Upload
                  listType="picture"
                  fileList={fileList}
                  onChange={handleImageChange}
                  className="lg:ms-4"
                  beforeUpload={() => false}
                >
                  <Button icon={<UploadOutlined />}>Upload Image</Button>
                </Upload>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <div className="mb-4">
            <label className="block text-gray-700 mb-2 text-left">
              Description:
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor"
                rows="4"
              />
            </label>
          </div>
        </div>

        <div className="mt-20 text-center">
          <button
            type="submit"
            className="py-2 px-6 w-40 mt-10 bg-themeColor1 text-themeColor2 font-semibold rounded-md shadow-sm hover:bg-themeColor2 hover:text-themeColor1"
          >
            SAVE
          </button>
        </div>

        <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      </form>
    </div>
  );
};

export default AddLedger;
